'use client';

import { useRef } from 'react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger } from '@/app/components/ui/select';
import { cn } from '@/app/utils';
import { HOME_SELECT_ITEM_CLASS_NAME, HomeSelectHighlight } from './HomeSelectHighlight';
import { useDelayedSelectClose } from './hooks/use-delayed-select-close';
import {
  HOME_DROPDOWN_CONTENT_CLASS_NAME,
  type HomeGenerationMethod,
  type HomeGenerationMode,
  type HomeGenerationType,
} from './utils';

interface GenerationOption {
  value: HomeGenerationType;
  label: string;
}

interface GenerationModeOption {
  value: HomeGenerationMode;
  label: string;
  type: HomeGenerationType;
  iconComponent?: React.ComponentType<{ className?: string }>;
}

interface HomeGenerationFormModeSelectsProps {
  generationMethodLabelMap: Record<HomeGenerationMethod, string>;
  generationModeOptions: GenerationModeOption[];
  generationTypeOptions: GenerationOption[];
  mobile?: boolean;
  mode: HomeGenerationMode;
  onGenerationMethodChange: (value: string) => void;
  onGenerationModeChange: (value: string) => void;
  onGenerationTypeChange: (value: string) => void;
}

const desktopSelectTriggerClassName =
  'h-9 rounded-10 border border-transparent bg-white/[0.04] px-3 text-system-white transition-colors hover:border-white/[0.12] hover:bg-white/[0.08] data-[state=open]:border-white/[0.12] data-[state=open]:bg-white/[0.08]';

const HomeGenerationFormModeSelectsDesktop = ({
  generationModeOptions,
  generationTypeOptions,
  mobile = false,
  mode,
  onGenerationModeChange,
}: {
  generationModeOptions: GenerationModeOption[];
  generationTypeOptions: GenerationOption[];
  mobile?: boolean;
  mode: HomeGenerationMode;
  onGenerationModeChange: (value: string) => void;
}) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const { open, onOpenChange, onValueChange } = useDelayedSelectClose(onGenerationModeChange);

  const currentOption = generationModeOptions.find((item) => item.value === mode);
  const CurrentIcon = currentOption?.iconComponent;

  return (
    <Select value={mode} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
      <SelectTrigger
        className={cn(
          desktopSelectTriggerClassName,
          mobile ? 'h-8 min-w-0 flex-1 gap-1 px-3 text-xs' : 'w-auto max-w-[260px] shrink-0 gap-1.5',
        )}
      >
        {CurrentIcon && <CurrentIcon className="size-5 shrink-0" />}
        <span className={cn('min-w-0 truncate', mobile ? 'text-xs' : 'text-[13px]')}>{currentOption?.label}</span>
      </SelectTrigger>
      <SelectContent
        className={cn(
          'relative w-[200px] border border-white/[0.08]',
          mobile && 'rounded-8 bg-[#20242B]',
          HOME_DROPDOWN_CONTENT_CLASS_NAME,
        )}
      >
        <div ref={listRef} className="relative">
          <HomeSelectHighlight containerRef={listRef} activeValue={mode} />
          {generationTypeOptions.map((typeOption) => {
            const options = generationModeOptions.filter((item) => item.type === typeOption.value);
            if (options.length === 0) {
              return null;
            }

            return (
              <SelectGroup key={typeOption.value}>
                <SelectLabel className="px-2 pt-1.5">{typeOption.label}</SelectLabel>
                {options.map((item) => {
                  const IconComponent = item.iconComponent;
                  return (
                    <SelectItem key={item.value} value={item.value} className={HOME_SELECT_ITEM_CLASS_NAME}>
                      <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="size-5 shrink-0" />}
                        <span>{item.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            );
          })}
        </div>
      </SelectContent>
    </Select>
  );
};

export const HomeGenerationFormModeSelects = ({
  generationModeOptions,
  generationTypeOptions,
  mobile = false,
  mode,
  onGenerationModeChange,
}: HomeGenerationFormModeSelectsProps) => {
  // PC 与 H5 共用「类型 + 方式」合并下拉，类型作为分组标题（不可选），方式作为选项。
  return (
    <HomeGenerationFormModeSelectsDesktop
      generationModeOptions={generationModeOptions}
      generationTypeOptions={generationTypeOptions}
      mobile={mobile}
      mode={mode}
      onGenerationModeChange={onGenerationModeChange}
    />
  );
};
